import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fmtPriceFromCents(c) {
  return `$${(Number(c || 0) / 100).toFixed(2)}`;
}

export default function SuperadminPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan id
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plan-configs");
      setPlans(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (p) => {
    setEditing(p.plan);
    setForm({
      display_name: p.display_name || "",
      description: p.description || "",
      monthly_price_cents: String(p.monthly_price_cents ?? 0),
      included_seats: String(p.included_seats ?? 1),
      default_markup_rate_percent: String(
        p.default_markup_rate_percent ?? 50
      ),
      default_trial_cents: String(p.default_trial_cents ?? 0),
      reason: "",
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(null);
  };

  const save = async () => {
    if (!editing || !form) return;
    if (!form.reason || form.reason.trim().length < 3) {
      toast.error("Reason required (3+ characters).");
      return;
    }
    const payload = {
      display_name: form.display_name,
      description: form.description,
      monthly_price_cents: Math.max(0, Number(form.monthly_price_cents) || 0),
      included_seats: Math.max(1, Number(form.included_seats) || 1),
      default_markup_rate_percent: Math.max(
        0,
        Math.min(1000, Number(form.default_markup_rate_percent) || 0)
      ),
      default_trial_cents: Math.max(0, Number(form.default_trial_cents) || 0),
      reason: form.reason.trim(),
    };
    try {
      await api.patch(`/admin/plan-configs/${editing}`, payload);
      toast.success("Plan updated.");
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="superadmin-plans">
      <h2 className="font-serif text-2xl tracking-tight text-[#2C2A29] mb-1">
        Plans
      </h2>
      <p className="font-sans text-sm text-[#7A7571] mb-6">
        Edit the four seeded plan tiers. Changes apply to{" "}
        <strong>future workspaces</strong> only — existing workspaces keep their
        current settings unless you reset them.
      </p>

      {loading ? (
        <div
          className="font-sans text-sm text-[#7A7571] py-8"
          data-testid="superadmin-plans-loading"
        >
          Loading plans…
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map((p) => {
            const isEditing = editing === p.plan;
            return (
              <div
                key={p.plan}
                data-testid={`plan-card-${p.plan}`}
                className="rounded-xl border border-[#DCD7CE] bg-white p-5"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-mono text-[#2C2A29] bg-[#EFEAE0]">
                        {p.plan}
                      </span>
                      <span className="font-serif text-lg text-[#2C2A29]">
                        {p.display_name}
                      </span>
                    </div>
                    <p className="font-sans text-xs text-[#7A7571] max-w-md">
                      {p.description}
                    </p>
                  </div>
                  {!isEditing ? (
                    <button
                      onClick={() => startEdit(p)}
                      data-testid={`plan-edit-${p.plan}`}
                      className="px-3 py-1.5 rounded-md font-sans text-sm text-[#2C2A29] border border-[#DCD7CE] bg-white hover:bg-[#EFEAE0] transition-colors"
                    >
                      Edit
                    </button>
                  ) : null}
                </div>

                {!isEditing ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <PlanStat
                      label="Monthly price"
                      value={fmtPriceFromCents(p.monthly_price_cents)}
                    />
                    <PlanStat
                      label="Included seats"
                      value={String(p.included_seats)}
                    />
                    <PlanStat
                      label="Default markup"
                      value={`${p.default_markup_rate_percent}%`}
                    />
                    <PlanStat
                      label="Default trial"
                      value={fmtPriceFromCents(p.default_trial_cents)}
                    />
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <EditField
                        label="Display name"
                        testid={`plan-edit-display-${p.plan}`}
                        value={form.display_name}
                        onChange={(v) =>
                          setForm((f) => ({ ...f, display_name: v }))
                        }
                      />
                      <EditField
                        label="Monthly price (cents)"
                        type="number"
                        testid={`plan-edit-price-${p.plan}`}
                        value={form.monthly_price_cents}
                        onChange={(v) =>
                          setForm((f) => ({ ...f, monthly_price_cents: v }))
                        }
                      />
                      <EditField
                        label="Included seats"
                        type="number"
                        testid={`plan-edit-seats-${p.plan}`}
                        value={form.included_seats}
                        onChange={(v) =>
                          setForm((f) => ({ ...f, included_seats: v }))
                        }
                      />
                      <EditField
                        label="Default markup (%)"
                        type="number"
                        testid={`plan-edit-markup-${p.plan}`}
                        value={form.default_markup_rate_percent}
                        onChange={(v) =>
                          setForm((f) => ({
                            ...f,
                            default_markup_rate_percent: v,
                          }))
                        }
                      />
                      <EditField
                        label="Default trial (cents)"
                        type="number"
                        testid={`plan-edit-trial-${p.plan}`}
                        value={form.default_trial_cents}
                        onChange={(v) =>
                          setForm((f) => ({ ...f, default_trial_cents: v }))
                        }
                      />
                    </div>
                    <EditField
                      label="Description"
                      testid={`plan-edit-description-${p.plan}`}
                      value={form.description}
                      onChange={(v) =>
                        setForm((f) => ({ ...f, description: v }))
                      }
                    />
                    <EditField
                      label="Reason for change (audit log)"
                      testid={`plan-edit-reason-${p.plan}`}
                      value={form.reason}
                      placeholder="e.g. price increase Q2"
                      onChange={(v) => setForm((f) => ({ ...f, reason: v }))}
                    />
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={save}
                        data-testid={`plan-save-${p.plan}`}
                        className="px-4 py-2 rounded-md font-sans text-sm font-medium bg-[#2C2A29] text-white hover:bg-[#1A1918] transition-colors"
                      >
                        Save plan
                      </button>
                      <button
                        onClick={cancel}
                        data-testid={`plan-cancel-${p.plan}`}
                        className="px-4 py-2 rounded-md font-sans text-sm text-[#2C2A29] border border-[#DCD7CE] bg-white hover:bg-[#EFEAE0] transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlanStat({ label, value }) {
  return (
    <div>
      <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-[#7A7571] font-semibold mb-1">
        {label}
      </p>
      <p className="font-mono text-sm text-[#2C2A29]">{value}</p>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="font-sans text-xs text-[#2C2A29] font-medium mb-1 block">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md border border-[#DCD7CE] bg-white font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C4A]/30 focus:border-[#D96C4A]"
      />
    </div>
  );
}
